import {
  Box,
  Heading,
  VStack,
  Text,
  Badge,
  HStack,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { ActionItem, Issue, User } from '../../types';
import { Card } from '../../../../components/Card';
import { Timestamp } from 'firebase/firestore';

interface CompletedItemsListProps {
  actionItems: ActionItem[];
  issues: Issue[];
  users: User[];
}

/**
 * Completed Items List Component
 * Shows completed action items and resolved issues
 */
export function CompletedItemsList({ actionItems, issues, users }: CompletedItemsListProps) {
  const headingColor = useColorModeValue('gray.900', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const getUserName = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    return user?.name || 'Unknown';
  };

  const formatDate = (date: Date | string | Timestamp | undefined) => {
    if (!date) return 'N/A';
    if (date instanceof Timestamp) {
      return date.toDate().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    }
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Card borderWidth="1px" borderColor={borderColor}>
        <Heading size="md" mb={4} color={headingColor}>
          Completed Items
        </Heading>
        <Tabs colorScheme="blue">
          <TabList>
            <Tab>Action Items ({actionItems.length})</Tab>
            <Tab>Issues ({issues.length})</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              {actionItems.length === 0 ? (
                <Text color={textColor}>No completed action items</Text>
              ) : (
                <VStack spacing={3} align="stretch">
                  {actionItems.map((actionItem) => (
                    <Box
                      key={actionItem.id}
                      p={3}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={borderColor}
                    >
                      <HStack justify="space-between" mb={1}>
                        <Text fontWeight="medium" color={headingColor} noOfLines={1}>
                          {actionItem.description}
                        </Text>
                        <Badge colorScheme="green">Completed</Badge>
                      </HStack>
                      <Text fontSize="xs" color={textColor}>
                        Completed: {formatDate(actionItem.completedDate)}
                        {actionItem.assignedToUserId && ` • Assigned to: ${getUserName(actionItem.assignedToUserId)}`}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              )}
            </TabPanel>
            <TabPanel px={0}>
              {issues.length === 0 ? (
                <Text color={textColor}>No resolved issues</Text>
              ) : (
                <VStack spacing={3} align="stretch">
                  {issues.map((issue) => (
                    <Box
                      key={issue.id}
                      p={3}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={borderColor}
                    >
                      <HStack justify="space-between" mb={1}>
                        <Text fontWeight="medium" color={headingColor} noOfLines={1}>
                          {issue.name}
                        </Text>
                        <Badge colorScheme="green">Resolved</Badge>
                      </HStack>
                      <Text fontSize="sm" color={textColor} noOfLines={2} mb={1}>
                        {issue.description}
                      </Text>
                      <Text fontSize="xs" color={textColor}>
                        Resolved: {formatDate(issue.solvedAt)}
                        {issue.ownerId && ` • Owner: ${getUserName(issue.ownerId)}`}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
    </Card>
  );
}

